import React, { useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import DatePicker from "react-datepicker";
import moment from "moment";
import { Query } from "react-apollo";
import gql from "graphql-tag";
import {
  Progress,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableCaption,
  Input,
  Select,
  Divider,
} from "@chakra-ui/react";

const freeTrialEventsQuery = gql`
  query freeTrialEvents($id: String!) {
    user(id: $id) {
      id
      freeTrialEvents {
        userId
        authorizedBy
        startDate
        endDate
        createdAt
        roles
        status
      }
    }
  }
`;

const INDUSTRY_OPTIONS = [
  { value: "", label: "Select Industry" },
  { value: "TECHNOLOGY", label: "Enterprise Technology" },
  { value: "FINANCIAL", label: "Financial Services" },
  { value: "OTHER", label: "Other" },
];

const REQUIRED_ERROR = "This is a required field.";

const getDefaultDates = (user) => {
  let formattedStartDate = null;
  let formattedEndDate = null;

  if (user.freeTrialStartDate && user.freeTrialEndDate) {
    formattedStartDate = new Date(user.freeTrialStartDate);
    formattedEndDate = new Date(user.freeTrialEndDate);
  }

  return [formattedStartDate, formattedEndDate];
};

const getDefaultValues = (user, userRoles) => {
  const [trialStartDate, trialEndDate] = getDefaultDates(user);
  const roleValues = {};
  for (const role of userRoles) {
    const hasRole = user?.roles.find((roleName) => roleName === role.name);
    roleValues[`roles.${role.name}`] = !!hasRole;
  }
  return {
    trialEndDate,
    trialStartDate,
    industry: user.industry || "",
    isFreeTrial: user.isFreeTrial || false,
    isActive: user.isActive,
    isDisableDeviceCheck: user.disableDeviceCheck,
    deviceLimit: user.deviceLimit || 2,
    excludeRespondents: user.excludeRespondents || false,
    ...roleValues,
  };
};

const getUniqueUserRoleGroups = (userRoles) => {
  const userRoleGroupMap = userRoles.reduce((acc, { group }) => {
    acc[group] = true;
    return acc;
  }, {});
  return Object.keys(userRoleGroupMap);
};

const UserForm = (props) => {
  const { user, viewer } = props;

  const {
    register,
    watch,
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: getDefaultValues(user, props.userRoles),
  });

  const watchIndustry = watch("industry");
  const watchIsFreeTrial = watch("isFreeTrial");
  const watchIsActive = watch("isActive");
  const watchIsDeviceCheckDisabled = watch("isDisableDeviceCheck");
  const watchStartDate = watch("trialStartDate");
  const watchEndDate = watch("trialEndDate");
  const watchDeviceLimit = watch("deviceLimit");

  const isFinancialOrOther =
    watchIndustry === "FINANCIAL" || watchIndustry === "OTHER";

  const isMounting = useRef(true);
  const refetchEvents = useRef(() => {});

  useEffect(() => {
    if (!isMounting.current && !user.isFreeTrial && watchIsFreeTrial) {
      setValue("trialStartDate", moment().startOf("day").toDate());

      if (watchIndustry === "TECHNOLOGY") {
        setValue("deviceLimit", 1);
        setValue("trialEndDate", moment().add(2, "day").endOf("day").toDate());
      } else {
        setValue("deviceLimit", 2);
        setValue("trialEndDate", moment().add(7, "day").endOf("day").toDate());
      }
    }
  }, [watchIsFreeTrial, watchIndustry]);

  useEffect(() => {
    isMounting.current = false;
  }, []);

  const userRoleGroups = getUniqueUserRoleGroups(props.userRoles).sort();

  const submitUser = async () => {
    const { mutate } = props;
    const formValues = getValues();

    const formatRoles = Object.keys(formValues.roles).filter((role) => {
      return formValues.roles[role];
    });

    formValues.isFreeTrial = !!watchIsFreeTrial;

    const userInfo = {
      name: formValues.name,
      email: formValues.email,
      organization: formValues.organization,
      industry: formValues.industry,
      isActive: formValues.isActive,
      isFreeTrial: formValues.isFreeTrial,
      freeTrialStartDate: watchIsFreeTrial
        ? moment(formValues.trialStartDate).startOf("day")
        : null,
      freeTrialEndDate: watchIsFreeTrial
        ? moment(formValues.trialEndDate).endOf("day")
        : null,
      disableDeviceCheck: formValues.isDisableDeviceCheck,
      roles: formatRoles,
      specialPermissions: {
        isPerformance: formValues.specialPermissions.isPerformance,
        isAdmin: false, // this will be set in the backend instead
      },
      requirePasswordReset: false,
      canStartTrial: false,
      deviceLimit: parseInt(formValues.deviceLimit),
      sendActivationEmail: formValues.sendActivationEmail,
      excludeRespondents: formValues.excludeRespondents,
    };

    if (user.id) {
      userInfo.id = user.id;
    }

    try {
      const { data, error } = await mutate({
        variables: userInfo,
      });

      props.submitResponse(data, error, ({ success }) => {
        if (success) {
          refetchEvents.current();
        }
      });
    } catch (e) {
      console.log("Error while submitting form.");
    }
  };

  const isEndDateSameOrAfterStartDate = () => {
    if (watchStartDate && watchEndDate) {
      return moment(watchEndDate).isSameOrAfter(moment(watchStartDate), "day");
    }
  };

  const canViewExcludeRespondents = viewer.roles.find((role) => {
    const roleName = role.toLowerCase();
    return roleName === "etr master admin" || roleName === "etr legal admin";
  });

  return (
    <>
      <form onSubmit={handleSubmit(submitUser)}>
        <div className="form-wrapper">
          <div className="left">
            <div className="form-section">
              <div className="section-header">
                <h5>User Information</h5>
                <input
                  data-cy="is-active"
                  type="checkbox"
                  defaultChecked={user.isActive}
                  {...register("isActive")}
                />
                <label>Active</label>
                {!watchIsActive && (
                  <span className="errors">
                    NOTE: User access has been disabled.
                  </span>
                )}
              </div>

              <ul className="user-info" data-cy="user-info-section">
                <li>
                  <label>Industry</label>
                  <Select
                    data-cy="industry"
                    defaultValue={watchIndustry}
                    {...register("industry", {
                      required: { value: true, message: REQUIRED_ERROR },
                    })}
                  >
                    {INDUSTRY_OPTIONS.map((option, idx) => {
                      return (
                        <option key={idx} value={option.value}>
                          {option.label}
                        </option>
                      );
                    })}
                  </Select>
                  {errors.industry && (
                    <span className="errors">{errors.industry.message}</span>
                  )}
                </li>

                <li>
                  <label>Name</label>
                  <Input
                    data-cy="name"
                    type="text"
                    defaultValue={user.name}
                    {...register("name", {
                      required: { value: true, message: REQUIRED_ERROR },
                    })}
                  />
                  {errors.name && (
                    <span className="errors">{errors.name.message}</span>
                  )}
                </li>

                <li>
                  <label>Email</label>
                  <Input
                    data-cy="email"
                    type="text"
                    defaultValue={user.email}
                    {...register("email", {
                      required: { value: true, message: REQUIRED_ERROR },
                    })}
                  />
                  {errors.email && (
                    <span className="errors">{errors.email.message}</span>
                  )}
                </li>

                <li>
                  <label>Organization</label>
                  <Input
                    data-cy="organization"
                    type="text"
                    defaultValue={user.organization}
                    {...register("organization", {
                      required: { value: true, message: REQUIRED_ERROR },
                    })}
                  />
                  {errors.organization && (
                    <span className="errors">
                      {errors.organization.message}
                    </span>
                  )}
                </li>
              </ul>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h5>Device Options</h5>
                <input
                  data-cy="disable-device-check"
                  type="checkbox"
                  {...register("isDisableDeviceCheck")}
                  defaultChecked={user.disableDeviceCheck}
                />
                <label>Disable device check</label>
              </div>

              {!watchIsDeviceCheckDisabled && (
                <>
                  <label>Allowed device limit</label>
                  <Input
                    data-cy="device-limit"
                    min={0}
                    type="number"
                    {...register("deviceLimit", {
                      required: !watchIsDeviceCheckDisabled,
                      min: 1,
                    })}
                    defaultValue={watchDeviceLimit}
                  />
                  {errors.deviceLimit &&
                    errors.deviceLimit.type === "required" && (
                      <span className="errors">{REQUIRED_ERROR}</span>
                    )}
                  {errors.deviceLimit && errors.deviceLimit.type === "min" && (
                    <span className="errors">
                      Value must be greater than 0.
                    </span>
                  )}
                </>
              )}
            </div>

            <div className="form-section free-trial">
              <div className="section-header">
                <h5>Free Trial Options</h5>

                <input
                  data-cy="is-free-trial"
                  defaultChecked={!!watchIsFreeTrial}
                  type="checkbox"
                  {...register("isFreeTrial")}
                />
                <label>Free Trial</label>
              </div>

              {watchIsFreeTrial && (
                <ul className="free-trial" data-cy="free-trial-options">
                  <li>
                    <label>Trial Start Date</label>
                    <Controller
                      name="trialStartDate"
                      control={control}
                      rules={{ required: watchIsFreeTrial }}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <DatePicker
                            wrapperClassName="trial-start-date-picker"
                            minDate={new Date()}
                            selected={value}
                            onChange={onChange}
                          />
                        );
                      }}
                    />
                    {errors.trialStartDate &&
                      errors.trialStartDate.type === "required" && (
                        <span className="errors">{REQUIRED_ERROR}</span>
                      )}
                  </li>

                  <li>
                    <label>Trial End Date</label>
                    <Controller
                      name="trialEndDate"
                      control={control}
                      rules={{
                        required: watchIsFreeTrial,
                        validate: () => {
                          return isEndDateSameOrAfterStartDate();
                        },
                      }}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <DatePicker
                            wrapperClassName="trial-end-date-picker"
                            onChange={onChange}
                            selected={value}
                            minDate={new Date()}
                          />
                        );
                      }}
                    />
                    {errors.trialEndDate &&
                      errors.trialEndDate.type === "required" && (
                        <span className="errors">{REQUIRED_ERROR}</span>
                      )}
                    {errors.trialEndDate &&
                      errors.trialEndDate.type === "validate" && (
                        <span className="errors">
                          End date cannot be before the start date.
                        </span>
                      )}
                  </li>
                </ul>
              )}
            </div>
            {props.user && props.user.id && (
              <Query
                query={freeTrialEventsQuery}
                variables={{ id: props.user.id }}
                fetchPolicy="cache-and-network"
              >
                {({ data, loading, error, refetch }) => {
                  refetchEvents.current = refetch;
                  if (error)
                    return <div>Error ocurred fetching free trial events.</div>;
                  if (loading) return <Progress size="xs" isIndeterminate />;
                  if (
                    data.user.freeTrialEvents &&
                    !!data.user.freeTrialEvents.length
                  ) {
                    return (
                      <Box overflow="auto" maxHeight="450px">
                        <Table variant="striped" data-cy="free-trial-events">
                          <TableCaption placement="top">
                            User Free Trial Logs
                          </TableCaption>
                          <Thead>
                            <Tr>
                              <Th> Created At </Th>
                              <Th> Authorized By </Th>
                              <Th> Start Date </Th>
                              <Th> End Date </Th>
                              <Th> Granted Roles </Th>
                              <Th> Status </Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {data.user.freeTrialEvents.map((event, idx) => {
                              return (
                                <Tr key={idx} data-cy={`event-row-${idx}`}>
                                  <Td>
                                    {moment(event.createdAt).format("lll")}
                                  </Td>
                                  <Td>{event.authorizedBy}</Td>
                                  <Td>
                                    {moment(event.startDate).format("lll")}
                                  </Td>
                                  <Td>{moment(event.endDate).format("lll")}</Td>
                                  <Td>
                                    <ul>
                                      {event.roles.map((role, idx) => {
                                        return <li key={idx}>{role}</li>;
                                      })}
                                    </ul>
                                  </Td>
                                  <Td data-cy={`event-row-status-${idx}`}>
                                    {event.status}
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </Box>
                    );
                  }
                  return null;
                }}
              </Query>
            )}
          </div>

          <div className="right">
            <div className="form-section">
              <div className="section-header">
                <h5 data-cy="special-permissions-header">
                  Special Permissions
                </h5>
              </div>
              <ul className="permissions">
                {Object.keys(user.specialPermissions).map(
                  (specialRole, idx) => {
                    const specialPermissionsList = ["isPerformance"];
                    const checked = !!user.specialPermissions[specialRole];

                    if (specialPermissionsList.includes(specialRole)) {
                      return (
                        <li key={idx}>
                          <input
                            type="checkbox"
                            defaultChecked={checked}
                            {...register(`specialPermissions.${specialRole}`)}
                          />
                          <label>{specialRole}</label>
                        </li>
                      );
                    }

                    return null;
                  }
                )}

                {canViewExcludeRespondents ? (
                  <li>
                    <input
                      data-cy="exclude-respondents"
                      type="checkbox"
                      defaultChecked={!!user.excludeRespondents}
                      {...register("excludeRespondents")}
                    />
                    <label>Government Custom Compliance</label>
                  </li>
                ) : null}
              </ul>

              <div className="form-section">
                <div className="section-header">
                  <h5 data-cy="access-permissions-header">User Access</h5>
                </div>
                <ul className="permissions">
                  {userRoleGroups.map((group, idx) => {
                    const canEditAdminRoles =
                      viewer.roles.includes("ETR Master Admin");
                    const isEtrAdminRoles = group.toLowerCase() === "etr admin";

                    const disableAdminSection =
                      !canEditAdminRoles && isEtrAdminRoles;

                    const groupRoles = props.userRoles
                      .filter((role) => {
                        return role.group === group;
                      })
                      .sort((a, b) =>
                        a.displayName.localeCompare(b.displayName)
                      );
                    const watchRoles = watch(
                      groupRoles.map((role) => `roles.${role.name}`)
                    );
                    return (
                      <div style={{ marginBottom: 16 }} key={group + "-" + idx}>
                        <fieldset
                          data-cy={`fieldset-${group
                            .replace(" ", "-")
                            .toLowerCase()}`}
                          disabled={disableAdminSection}
                        >
                          <li
                            style={{
                              backgroundColor: "#efefef",
                              padding: "8px",
                            }}
                          >
                            <input
                              type="checkbox"
                              id={group}
                              checked={watchRoles.every(Boolean)}
                              onChange={(e) => {
                                groupRoles.forEach((role) => {
                                  setValue(
                                    `roles.${role.name}`,
                                    e.target.checked
                                  );
                                });
                              }}
                            />
                            <label htmlFor={group}>{group}</label>
                          </li>
                          {groupRoles.map((role) => {
                            const legacyFreeTrialRole = "epp - trial";
                            return (
                              <li
                                key={group + "-" + role.name}
                                style={{
                                  marginLeft: 8,
                                }}
                              >
                                <input
                                  id={group + "-" + role.name}
                                  disabled={
                                    role.name.toLowerCase() ===
                                    legacyFreeTrialRole
                                  }
                                  type="checkbox"
                                  defaultChecked={user?.roles.includes(
                                    role.name
                                  )}
                                  {...register(`roles.${role.name}`)}
                                />
                                <label htmlFor={group + "-" + role.name}>
                                  {role.displayName}
                                  {role.description ? (
                                    <span style={{ color: "#9d9693" }}>
                                      {" "}
                                      {role.description}
                                    </span>
                                  ) : null}
                                </label>
                              </li>
                            );
                          })}
                        </fieldset>
                      </div>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          <div className="bottom">
            {!user.id && isFinancialOrOther && (
              <div className="form-section">
                <div className="section-header">
                  <h5>Activation Email Options</h5>
                  <input
                    data-cy="send-activation-email"
                    type="checkbox"
                    {...register("sendActivationEmail")}
                  />
                  <label>Auto Generate Email</label>
                </div>
              </div>
            )}
            <Divider />
          </div>

          <button data-cy="submit" type="submit" className="btn btn-primary">
            Submit
          </button>
        </div>
      </form>
    </>
  );
};

export default UserForm;
